import { FormikHelpers, FormikProps, useFormik } from "formik";
import { FieldMetaData, Fields } from "../types/SchemaFactory";
import { AnyObject, ObjectSchema } from "yup";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import languageServiceFactory from "../services/LanguageServiceImpl";
import _ from "lodash";

interface UseCustomFormikProps<T> {
    fields: Fields<T>;
    schema: ObjectSchema<AnyObject>;
    initial: T | Partial<T>;
    validateOnMount?: boolean;
    onSubmit: (values: T, formikHelpers: FormikHelpers<T>) => Promise<void>;
}

interface UseCustomFormik<T> {
    form: FormikProps<T>;
    errorKey: string;
    setErrorKey: Dispatch<SetStateAction<string>>;
}

const languageService = languageServiceFactory();

function useCustomFormik<FieldsType>({
    fields,
    schema,
    initial,
    validateOnMount,
    onSubmit,
}: UseCustomFormikProps<FieldsType>): UseCustomFormik<FieldsType> {
    const [errorKey, setErrorKey] = useState("");

    function getInitial(key: keyof FieldsType, fieldMeta: FieldMetaData) {
        if (fieldMeta.type === "select") {
            return _.get(fieldMeta, "initial.value");
        }
        if (fieldMeta.array) {
            const init = _.get(initial, key, []);
            return init.length ? init : _.get(fieldMeta, "initial", []);
        }
        if (initial) {
            return _.get(initial, key) || _.get(fieldMeta, "initial");
        }
        return _.get(fieldMeta, "initial");
    }

    const initialValues = Object.entries(fields).reduce((acc, [key, value]) => {
        (acc as any)[key] = getInitial(
            key as keyof FieldsType,
            value as FieldMetaData
        );
        return acc;
    }, {} as FieldsType);

    const form = useFormik({
        initialValues,
        validateOnMount,
        validationSchema: schema,
        onSubmit: async (
            values: FieldsType,
            formikHelpers: FormikHelpers<FieldsType>
        ) => {
            setErrorKey("");
            formikHelpers.setSubmitting(true);
            try {
                await onSubmit(values, formikHelpers);
            } catch (err) {
                setErrorKey("errors.common");
            }
        },
        enableReinitialize: true,
    });

    useEffect(() => {
        if (Object.values(form.errors).length) {
            form.validateForm();
        }
    }, [languageService.currentLang]);

    return { form, errorKey, setErrorKey };
}

export default useCustomFormik;
